import { AxiosResponse } from "axios";

import {
  DistanceCountPayloadType,
  UpdateEWayBillPayloadType,
  UpdateTrasporterEWayBillPayloadType,
} from "@/redux/invoicingAdvance/invoicingAdvance-types";

import { deleteData, getData, postData, putData } from "@/apis/apiInstance";

import {
  AddCommentPayloadType,
  AddInvoicingAdvanceApiPayloadType,
  BankStatementTransactionFileUploadPayload,
  BulkUploadApiType,
  CreateOthersInvoiceAndAllocatePayload,
  DeleteInvoiceEntryPayloadType,
  DeleteOtherInvoicesApiPayloadType,
  DownloadInvoicingAdvanceReportsApiPayloadType,
  DownloadOthersReportsApiPayloadType,
  DuplicateEntriesPayloadType,
  EinvoiceAuthenticationDataPayloadType,
  EWayBillCancelPayloadType,
  EWayBillPayloadDownloadType,
  ExtendEWayBillPayloadType,
  GenerateEWayBillPayloadType,
  GetAllBusinessAddressesApiPayloadType,
  GetAllCustomerSupplierApiPayload,
  GetAllExpensesInvoicesListPayloadType,
  GetAllLedgersListApiPayloadType,
  GetAllMappedClientColumnsApiPayloadType,
  GetAllOtherInvoicesListApiPayloadType,
  GetBankStatementsTransactionsCommentListPayload,
  GetBankStatementsTransactionsListPayload,
  GetBulkUploadHistoryPayloadType,
  GetCommentApiType,
  GetGstrTwoATwoBInvoicePayload,
  GetSingleInvoiceEntryApiPayloadType,
  GetSingleOtherInvoiceDetailsPayloadType,
  GetUploadedBankStatementPayload,
  InvoiceHistoryPayloadType,
  InvoiceOverdueEmailTimelinePayload,
  InvoicingAdvanceLockEntryApiPayloadType,
  InvoicingAdvanceNotesPayloadType,
  LockUnlockInvoicePayloadType,
  OtherHistoryPayloadApiType,
  PostInvoicingAdvanceOCRApiPayloadType,
  PostMultipleBankTransactionPayload,
  PostTransactionsCommentListPayload,
  RefreshBankStatementSuggestionsPayload,
  ReviewEWayBillPayloadApi,
  SendInvoiceGenerationEmailPayloadApiType,
  SendOverdueEmailPayloadApiType,
  UpdateInvoicingAdvanceApiPayloadType,
  UploadBankStatementPayload,
} from "@/components/InvoicingAdvance/apis/invoicingAdvanceApis-type";
import {
  AddOtherInvoiceApiPayloadType,
  ApproveInvoiceApiPayloadType,
  UpdateOtherInvoiceApiPayloadType,
} from "@/components/InvoicingAdvance/helpers/invoicingAdvance-type";

import { GetAllLedgersListPayloadType } from "@/commonTypes/clientSettingsTypes";

import { getCSRFToken } from "@/utils/commonFunctions";

export async function getAllExpensesInvoicesListApi(
  data: GetAllExpensesInvoicesListPayloadType
): Promise<AxiosResponse> {
  const { token, params } = data;

  return await getData(`invoice-generation/customer/invoice/list`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function invoicingAdvanceGetComment(
  payload: GetCommentApiType
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await getData(`invoice-generation/customer/invoice/comments`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function invoicingAdvancePostCommentApi(
  payload: AddCommentPayloadType
): Promise<AxiosResponse> {
  const { token, data } = payload;

  return await postData(
    `invoice-generation/add/customer/invoice/comments`,
    data,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function getInvoiceHistoryApi(
  data: InvoiceHistoryPayloadType
): Promise<AxiosResponse> {
  const { token, params } = data;

  return await getData(`history/invoice`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function downloadInvoicingAdvanceReportsApi(
  data: DownloadInvoicingAdvanceReportsApiPayloadType
): Promise<AxiosResponse> {
  const { params, token } = data;

  return await getData(`invoice-generation/invoice/download`, {
    params: params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function downloadOthersReportsApi(
  data: DownloadOthersReportsApiPayloadType
): Promise<AxiosResponse> {
  const { params, token } = data;

  return await getData(`invoice-generation/others/invoice/download`, {
    params: params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function invoicingAdvanceLockEntryApi(
  payload: InvoicingAdvanceLockEntryApiPayloadType
): Promise<AxiosResponse> {
  const { token, data } = payload;

  return await postData(`invoice-generation/client/lock`, data, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getAllMappedClientColumnListApi(
  data: GetAllMappedClientColumnsApiPayloadType
): Promise<AxiosResponse> {
  const { token, params } = data;

  return await getData(`invoice-generation/client/column/list`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getAllBusinessAddressesApi(
  data: GetAllBusinessAddressesApiPayloadType
): Promise<AxiosResponse> {
  const { token, params } = data;

  return getData(`invoice-generation/address/list`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getAllCustomerSupplierApi(
  data: GetAllCustomerSupplierApiPayload
): Promise<AxiosResponse> {
  const { token, params } = data;

  return await getData(`invoice-generation/customer/supplier/list`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getInvoicingAdvanceNotesListApi(
  payload: InvoicingAdvanceNotesPayloadType
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await getData(`invoice-generation/invoice/notes`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getAllLedgersListApi(
  data: GetAllLedgersListApiPayloadType
): Promise<AxiosResponse> {
  const { token, params } = data;

  return getData(`invoice-generation/ledger/list`, {
    params: params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function postInvoicingAdvanceOCRApi(
  data: PostInvoicingAdvanceOCRApiPayloadType
): Promise<AxiosResponse> {
  const { token, formData, drive_file_id, client_code, invoice_type } = data;

  if (drive_file_id && client_code) {
    return await postData(
      `invoice-generation/optical/character/recognition/model`,
      { drive_file_id, client_code, invoice_type },
      {
        headers: {
          "Content-Type": "application/json",
          "user-token": token,
        },
      }
    );
  }

  return await postData(
    `invoice-generation/optical/character/recognition/model`,
    formData,
    {
      headers: {
        "user-token": token,
      },
    }
  );
}

export async function addInvoicingAdvanceApi(
  data: AddInvoicingAdvanceApiPayloadType
): Promise<AxiosResponse> {
  const { token, payload } = data;

  return await postData(`invoice-generation/add/customer/invoice`, payload, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getSingleInvoiceEntryApi(
  data: GetSingleInvoiceEntryApiPayloadType
): Promise<AxiosResponse> {
  const { token, params } = data;

  return await getData(`invoice-generation/customer/invoice`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function invoiceApprovalApi(
  payload: ApproveInvoiceApiPayloadType
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await postData(`invoice-generation/approve/customer/invoice`, params, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function invoiceDownloadDetailsApi(
  payload: GetCommentApiType
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await getData(`invoice-generation/customer/invoice/download`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function deleteInvoiceEntryApi(
  deleteInvoice: DeleteInvoiceEntryPayloadType
): Promise<AxiosResponse> {
  const { token, params } = deleteInvoice;

  return await deleteData(`invoice-generation/delete/customer/invoice`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function updateInvoicingAdvanceApi(
  data: UpdateInvoicingAdvanceApiPayloadType
): Promise<AxiosResponse> {
  const { token, payload } = data;

  return await postData(`invoice-generation/update/customer/invoice`, payload, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getBulkUploadHistoryDataApi(
  data: GetBulkUploadHistoryPayloadType
): Promise<AxiosResponse> {
  const { token, params } = data;

  return await getData(`invoice-generation/bulk-upload/history`, {
    params: params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function bulkUploadOtherApi(
  payload: BulkUploadApiType
): Promise<AxiosResponse> {
  const { token, formData } = payload;

  return await postData(`invoice-generation/upload/others/invoice`, formData, {
    headers: {
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function bulkUploadOtherSuspenseApi(
  payload: BulkUploadApiType
): Promise<AxiosResponse> {
  const { token, formData } = payload;

  return await postData(
    `invoice-generation/upload-suspense/others/invoice`,
    formData,
    {
      headers: {
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function bulkUploadRevenueApi(
  payload: BulkUploadApiType
): Promise<AxiosResponse> {
  const { token, formData } = payload;

  return await postData(`invoice-generation/upload/invoices`, formData, {
    headers: {
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function bulkUploadExpensesApi(
  payload: BulkUploadApiType
): Promise<AxiosResponse> {
  const { token, formData } = payload;

  return await postData(`invoice-generation/upload/invoices`, formData, {
    headers: {
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function ReviewEWayBillApi(
  payload: ReviewEWayBillPayloadApi
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await getData(`invoice-generation/e-way-bill/review/details`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function generateEWayBillApi(
  data: GenerateEWayBillPayloadType
): Promise<AxiosResponse> {
  const { token, data: payload } = data;

  return await postData(`invoice-generation/generate/e-way-bill`, payload, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function eWayBillCancelApi(
  data: EWayBillCancelPayloadType
): Promise<AxiosResponse> {
  const { token, data: payload } = data;

  return await postData(`invoice-generation/cancel/e-way-bill`, payload, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function extendEWayBillApi(
  data: ExtendEWayBillPayloadType
): Promise<AxiosResponse> {
  const { token, data: payload } = data;

  return await postData(`invoice-generation/extend/e-way-bill`, payload, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function eWayBillPayloadDownloadApi(
  data: EWayBillPayloadDownloadType
): Promise<AxiosResponse> {
  const { token, data: payload } = data;

  return await postData(
    `invoice-generation/e-way-bill/payload/download`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function checkDistanceCountApi(
  data: DistanceCountPayloadType
): Promise<AxiosResponse> {
  const { token, data: payload } = data;

  return await getData(`invoice-generation/e-way-bill/get/distance`, {
    params: payload,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function eWayBillAuthenticationPostApi(
  payload: EinvoiceAuthenticationDataPayloadType
): Promise<AxiosResponse> {
  const { token, data } = payload;

  return await postData(`quicko-e-way-bill/authentication`, data, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function updateTransporterEWayBillDetailsApi(
  data: UpdateTrasporterEWayBillPayloadType
): Promise<AxiosResponse> {
  const { token, data: payload } = data;

  return await putData(
    `invoice-generation/update/transporter-detail/e-way-bill`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function updateVehicleEWayBillDetailsApi(
  data: UpdateEWayBillPayloadType
): Promise<AxiosResponse> {
  const { token, data: payload } = data;

  return await putData(
    `invoice-generation/update/vehicle-details/e-way-bill`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function checkDuplicateInvoiceEntryApi(
  payload: DuplicateEntriesPayloadType
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await getData(`/invoice-generation/is/duplicate/invoice`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getAllOtherInvoicesListApi(
  data: GetAllOtherInvoicesListApiPayloadType
): Promise<AxiosResponse> {
  const { token, params } = data;
  return await getData(`invoice-generation/others/invoice`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function deleteOtherInvoiceApi(
  deleteInvoice: DeleteOtherInvoicesApiPayloadType
): Promise<AxiosResponse> {
  const { token, params } = deleteInvoice;

  return await deleteData(`invoice-generation/delete/others/invoice`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getOthersInvoiceHistoryApi(
  data: OtherHistoryPayloadApiType
): Promise<AxiosResponse> {
  const { token, params } = data;

  return await getData(`history/others/invoice`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function sendOverdueEmailApi(
  payload: SendOverdueEmailPayloadApiType
): Promise<AxiosResponse> {
  const { token, data } = payload;

  return await postData(`notification-module/invoice-payment`, data, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function addOtherInvoiceGenerationApi(
  data: AddOtherInvoiceApiPayloadType
): Promise<AxiosResponse> {
  const { token, payload } = data;

  return await postData(`invoice-generation/add/others/invoice`, payload, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getSingleOtherInvoiceDetailsApi(
  data: GetSingleOtherInvoiceDetailsPayloadType
): Promise<AxiosResponse> {
  const { token, params } = data;

  return await getData(`invoice-generation/others/invoice/id`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function updateOtherInvoiceApi(
  data: UpdateOtherInvoiceApiPayloadType
): Promise<AxiosResponse> {
  const { token, payload } = data;

  return await postData(`invoice-generation/update/others/invoice`, payload, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getGstr2a2bComparePayloadApi(
  data: GetGstrTwoATwoBInvoicePayload
): Promise<AxiosResponse> {
  const { token, params } = data;

  return getData("gst/get/add/gstr-2a/gstr-2b/invoice/payload", {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function sendInvoiceGenerationEmailApi(
  payload: SendInvoiceGenerationEmailPayloadApiType
): Promise<AxiosResponse> {
  const { token, ...data } = payload;

  return await postData(`notification-module/send/invoice/email`, data, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getUploadedBankStatementApi(
  data: GetUploadedBankStatementPayload
): Promise<AxiosResponse> {
  const { token, params } = data;

  return getData("invoice-generation/get/uploaded/bank/statement/list", {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
export async function getAllPaymentLedgersListApi(
  data: GetAllLedgersListPayloadType
): Promise<AxiosResponse> {
  const { client_code, token, data_req } = data;

  return getData(`invoice-generation/ledger/list/based_on_type`, {
    params: {
      client_code,
      ledger_types: "payment",
      data_req,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function uploadBankStatementApi(
  payload: UploadBankStatementPayload
): Promise<AxiosResponse> {
  const { token, formData } = payload;

  return await postData(`invoice-generation/upload/bank/statement`, formData, {
    headers: {
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getUploadedBankStatementTransactionApi(
  payload: GetBankStatementsTransactionsListPayload
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await getData(
    `invoice-generation/get/bank/statement/transactions/list`,
    {
      params,
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
      },
    }
  );
}

export async function allocateBankTransactionToOtherInvoiceApi(
  payload: CreateOthersInvoiceAndAllocatePayload
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await postData(
    `invoice-generation/bank/transaction/create/others-invoice/allocate`,
    params,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function postMultipleBankTransactionApi(
  payload: PostMultipleBankTransactionPayload
): Promise<AxiosResponse> {
  const { token, data } = payload;

  return await postData(
    `invoice-generation/multiple/bank/transaction/create/others-invoice`,
    data,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function getUploadedBankStatementTransactionCommentApi(
  payload: GetBankStatementsTransactionsCommentListPayload
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await getData(
    `invoice-generation/get/bank/transaction/comments/list`,
    {
      params,
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
      },
    }
  );
}
export async function postTransactionCommentApi(
  payload: PostTransactionsCommentListPayload
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await postData(
    `invoice-generation/add/bank/transaction/comment`,
    params,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function refreshBankStatementSuggestionsApi(
  payload: RefreshBankStatementSuggestionsPayload
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await postData(
    `invoice-generation/bank/statement/refresh/suggestions`,
    params,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function bankStatementTransactionFileUploadApi(
  payload: BankStatementTransactionFileUploadPayload
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await postData(
    `invoice-generation/bank/statement/transaction/file/upload`,
    params,
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function getOverdueInvoiceReminderEmailTimeline(
  payload: InvoiceOverdueEmailTimelinePayload
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await getData(`notification-module/invoice-email/read-status`, {
    params,
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function lockUnlockInvoiceApi(
  payload: LockUnlockInvoicePayloadType
): Promise<AxiosResponse> {
  const { token, params } = payload;

  return await postData(`invoice-generation/invoice/lock`, params, {
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
